import React, { useState, useEffect } from 'react';
import { Text, Image } from '@sitecore-jss/sitecore-jss-react';
import { useTranslation } from 'react-i18next';
import { useSite } from '../../contexts/SiteContext';

/**
 * Helper function to extract site value from various field formats
 */
const getSiteValue = (siteField) => {
  if (!siteField) return '';
  
  if (typeof siteField === 'string') {
    return siteField;
  } else if (siteField?.url) {
    // Extract the last part of the URL path (e.g., "KSA" from "/Content/Settings/SiteOptions/KSA")
    const urlParts = siteField.url.split('/');
    return urlParts[urlParts.length - 1] || '';
  } else if (typeof siteField?.value === 'string') {
    return siteField.value;
  } else if (siteField?.value?.name) {
    return siteField.value.name;
  } else if (siteField?.name) {
    return siteField.name;
  } else if (siteField?.displayName) {
    return siteField.displayName;
  }
  return '';
};

/**
 * Hero Banner component with background color, logo, title, and language switch.
 * Supports site-specific hero items (KSA/UAE) similar to Footer component.
 */
const HeroBanner = ({ fields }) => {
  const { i18n } = useTranslation();
  const site = useSite();
  const [isClient, setIsClient] = useState(false);
  const isRTL = i18n.language === 'ar' || i18n.language === 'ar-SA';

  // Force re-render on client after hydration to get correct site context
  useEffect(() => {
    setIsClient(true);
  }, []);
  
  // Handle cases where fields might be undefined
  if (!fields) {
    return null;
  }

  // Don't render until client-side hydration completes to prevent FOUC
  // This prevents KSA header from flashing before UAE header appears
  if (!isClient) {
    return (
      <div className="hero-banner" style={{ minHeight: '100px', backgroundColor: 'transparent' }}>
        {/* Placeholder to prevent layout shift */}
      </div>
    );
  }

  const currentSite = site.isUAE ? 'UAE' : 'KSA';

  // Get hero items and filter by current site
  const heroItems = fields.heroItems || [];

  // First, try to find an exact match for current site
  let matchingItem = heroItems.find(item => {
    const itemSite = getSiteValue(item?.fields?.site);
    return itemSite && itemSite.toUpperCase() === currentSite;
  });

  // If no exact match, fall back to an item with 'ALL' or no site specified
  if (!matchingItem) {
    matchingItem = heroItems.find(item => {
      const itemSite = getSiteValue(item?.fields?.site);
      return !itemSite || itemSite.toUpperCase() === 'ALL';
    });
  }

  // Determine which fields to use - from matching hero item or fallback to direct fields
  const backgroundColor = matchingItem?.fields?.backgroundColor?.value || fields.backgroundColor?.value || '#007bff';
  const logo = matchingItem?.fields?.logo || fields.logo;
  const logoNavigationLink = matchingItem?.fields?.logoNavigationLink?.value?.href || null;
  const title = matchingItem?.fields?.title || fields.title;

  const toggleLanguage = () => {
    // Get the new language
    const newLang = isRTL ? 'en' : 'ar';
    
    // Get current path and remove language prefix if exists
    const currentPath = window.location.pathname;
    let basePath = currentPath;
    
    if (currentPath.startsWith('/ar')) {
      basePath = currentPath.substring(3) || '/';
    } else if (currentPath.startsWith('/en')) {
      basePath = currentPath.substring(3) || '/';
    }
    
    // Ensure basePath starts with /
    if (!basePath.startsWith('/')) {
      basePath = '/' + basePath;
    }
    
    // Redirect to new language URL
    const newUrl = `/${newLang}${basePath === '/' ? '' : basePath}`;
    window.location.href = newUrl;
  };

  const bannerStyle = {
    backgroundColor: backgroundColor,
    padding: '20px',
    textAlign: 'center',
    color: '#ffffff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100px',
  };

  const bannerContentStyle = {
    maxWidth: '900px',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  };

  const logoStyle = {
    width: '300px',
    height: '80px',
    flex: '1',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const titleStyle = {
    fontSize: '2rem',
    fontWeight: 'bold',
    margin: '0',
    flex: '2',
  };

  const languageSwitchStyle = {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    padding: '10px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'opacity 0.3s ease',
  };

  return (
    <div className="hero-banner" style={bannerStyle} key={`hero-${currentSite}-${isClient}`}>
      <div style={bannerContentStyle}>
      {logo && logo.value && (
        logoNavigationLink ? (
          <a href={logoNavigationLink} className="hero-logo" style={{ ...logoStyle, textDecoration: 'none' }}>
            <Image 
              field={logo} 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </a>
        ) : (
          <div className="hero-logo" style={logoStyle}>
            <Image 
              field={logo} 
              style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            />
          </div>
        )
      )}
      {title && (
        <Text 
          tag="h1" 
          className="hero-title" 
          field={title}
          style={titleStyle}
        />
      )}
      <button
        onClick={toggleLanguage}
        style={languageSwitchStyle}
        onMouseEnter={(e) => e.target.style.opacity = '0.8'}
        onMouseLeave={(e) => e.target.style.opacity = '1'}
        title={i18n.language === 'en' ? 'العربية' : 'English'}
      >
        <svg width="30px" height="30px" viewBox="0 0 30 30">
          <g id="Icon-Language" stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
            <g id="translation" transform="translate(2.000000, 1.000000)" fill="#FFFFFF" fillRule="nonzero">
              <path className="stroke" d="M10.8183329,14.608761 C10.8183329,14.3926927 10.6431622,14.2175219 10.4270939,14.2175219 L1.64570785,14.2175219 C1.16970036,14.2175219 0.782478056,13.8302996 0.782478056,13.3542922 L0.782478056,1.64570785 C0.782478056,1.16970036 1.16970036,0.782478056 1.64570785,0.782478056 L13.3542922,0.782478056 C13.8302996,0.782478056 14.2175219,1.16970036 14.2175219,1.64570785 L14.2175219,10.4270939 C14.2175219,10.6431622 14.3926927,10.8183329 14.608761,10.8183329 C14.8248292,10.8183329 15,10.6431622 15,10.4270939 L15,1.64570785 C15,0.738241963 14.261758,2.27373675e-13 13.3542922,2.27373675e-13 L1.64570785,2.27373675e-13 C0.738241963,2.27373675e-13 0,0.738241963 0,1.64570785 L0,13.3542922 C0,14.261758 0.738241963,15 1.64570785,15 L10.4270939,15 C10.6431622,15 10.8183329,14.8248292 10.8183329,14.608761 Z" id="Path"></path>
              <path className="stroke" d="M25.3542922,12 L22.0089898,12 C21.7929215,12 21.6177508,12.1751708 21.6177508,12.391239 C21.6177508,12.6073073 21.7929215,12.7824781 22.0089898,12.7824781 L25.3542922,12.7824781 C25.8302996,12.7824781 26.2175219,13.1697004 26.2175219,13.6457078 L26.2175219,25.3542922 C26.2175219,25.8302996 25.8302996,26.2175219 25.3542922,26.2175219 L13.6457078,26.2175219 C13.1697004,26.2175219 12.7824781,25.8302996 12.7824781,25.3542922 L12.7824781,13.6457078 C12.7824781,13.1697004 13.1697004,12.7824781 13.6457078,12.7824781 L20.3363647,12.7824781 C20.552433,12.7824781 20.7276037,12.6073073 20.7276037,12.391239 C20.7276037,12.1751708 20.552433,12 20.3363647,12 L13.6457078,12 C12.738242,12 12,12.738242 12,13.6457078 L12,25.3542922 C12,26.261758 12.738242,27 13.6457078,27 L25.3542922,27 C26.261758,27 27,26.261758 27,25.3542922 L27,13.6457078 C27,12.738242 26.261758,12 25.3542922,12 Z" id="Path"></path>
              <path className="stroke" d="M10.849006,21.1652449 L9.23518161,19.9901573 C9.06843386,19.8687297 8.83177586,19.9015664 8.70670246,20.0634537 C8.58162906,20.225341 8.61545172,20.4551007 8.78219948,20.5765284 L9.49005956,21.0919486 L8.20172804,21.0919486 C6.85254593,21.0919486 5.75497022,20.0263169 5.75497022,18.7165105 L5.75497022,16.3664819 C5.75497022,16.1640862 5.58595755,16 5.37748511,16 C5.16901267,16 5,16.1640862 5,16.3664819 L5,18.7165105 C5,20.430522 6.43630568,21.8249124 8.20172804,21.8249124 L9.49005956,21.8249124 L8.78219948,22.3403326 C8.61545172,22.4617602 8.58162906,22.69152 8.70670246,22.8534072 C8.78089086,22.9494255 8.89423706,23 9.00899253,23 C9.08781143,23 9.16728462,22.9761054 9.23518161,22.9267036 L10.849006,21.751616 C10.9440819,21.6823754 11,21.573799 11,21.4584305 C11,21.343062 10.9440819,21.2344367 10.849006,21.1652449 Z" id="Path"></path>
              <path className="stroke" d="M16.150994,5.83476971 L17.7648184,7.00985398 C17.8327154,7.05930447 17.9121886,7.08315016 17.9910075,7.08315016 C18.1057629,7.08315016 18.2191595,7.03252693 18.2932975,6.93655781 C18.4183709,6.77467099 18.3845483,6.54491191 18.2178005,6.42348458 L17.5099404,5.90806588 L18.798272,5.90806588 C20.1474541,5.90806588 21.2450298,6.97369453 21.2450298,8.28349718 L21.2450298,10.6335191 C21.2450298,10.8359143 21.4140424,11 21.6225149,11 C21.8309873,11 22,10.8359143 22,10.6335191 L22,8.28349718 C22,6.56949056 20.5636943,5.17510414 18.798272,5.17510414 L17.5099404,5.17510414 L18.2178005,4.65968544 C18.3845483,4.53825811 18.4183709,4.30849903 18.2932975,4.14661221 C18.1681738,3.98467653 17.9316165,3.95188871 17.7648184,4.07331604 L16.150994,5.24840031 C16.0559181,5.31764076 16,5.42621683 16,5.54158501 C16,5.65695319 16.0559181,5.76557812 16.150994,5.83476971 Z" id="Path"></path>
              <path className="stroke" d="M7.64686265,10.6054688 C8.04541337,10.6054688 8.43819993,10.5531812 8.82522233,10.448606 C9.21224473,10.3440308 9.53092062,10.2070013 9.78125,10.0375174 L9.78125,10.0375174 L9.78125,8.88538761 L9.72690217,8.88538761 C9.49962945,9.06929566 9.18013011,9.23607502 8.76840415,9.38572568 C8.35667819,9.53537635 7.97789032,9.61020168 7.63204051,9.61020168 C7.00292325,9.61020168 6.5072052,9.46325554 6.14488636,9.16936327 C5.78256752,8.875471 5.6014081,8.49052622 5.6014081,8.01452893 C5.6014081,7.68998532 5.69528162,7.39519154 5.88302866,7.13014759 C6.07077569,6.86510364 6.35404315,6.66046086 6.73283103,6.51621926 L6.73283103,6.51621926 L8.82769269,5.71026929 L8.82769269,4.76909282 L6.47097332,5.68863305 C6.34910244,5.62011829 6.22887846,5.4957099 6.11030138,5.3154079 C5.99172431,5.13510589 5.93243577,4.94939483 5.93243577,4.7582747 C5.93243577,4.41930693 6.03619071,4.14254335 6.24370059,3.92798396 C6.45121047,3.71342458 6.74930007,3.60614488 7.13796937,3.60614488 C7.30924736,3.60614488 7.49123024,3.62687961 7.68391798,3.66834907 C7.87660573,3.70981854 8.0157691,3.74317441 8.1014081,3.76841669 L8.1014081,3.76841669 L8.15081522,3.76841669 L8.15081522,2.75151337 C8.11458333,2.74430129 7.99024209,2.7181575 7.7777915,2.673082 C7.56534091,2.6280065 7.33889163,2.60546875 7.09844368,2.60546875 C6.51214592,2.60546875 6.03536726,2.80199794 5.66810771,3.19505631 C5.30084816,3.58811468 5.11721838,4.10017238 5.11721838,4.7312294 C5.11721838,5.03053073 5.19709321,5.3063928 5.35684289,5.55881561 C5.51659256,5.81123841 5.66234354,5.97891928 5.79409585,6.0618582 C5.47459651,6.28182665 5.22591403,6.55678721 5.04804842,6.88673988 C4.87018281,7.21669255 4.78125,7.59622827 4.78125,8.02534705 C4.78125,8.80064567 5.03322628,9.42449061 5.53717885,9.89688187 C6.04113142,10.3692731 6.74435935,10.6054688 7.64686265,10.6054688 Z" id="ع"></path>
            </g>
            <g id="Group" transform="translate(19.000000, 17.000000)" stroke="#FFFFFF" strokeLinecap="round" strokeWidth="0.8">
              <path className="stroke" d="M5.25,0 L1,0 C0.44771525,1.01453063e-16 -6.76353751e-17,0.44771525 0,1 L0,6 C6.76353751e-17,6.55228475 0.44771525,7 1,7 L5.25,7 L5.25,7" id="Path-2"></path>
              <line className="stroke" x1="4.375" y1="3.5" x2="0" y2="3.5" id="Path-3"></line>
            </g>
          </g>
        </svg>
      </button>
      </div>
    </div>
  );
};

export default HeroBanner;
